/**
 * What the command palette lists (TMMPDM-0040, SEARCH-001/002/004/005, FEED-005).
 *
 * - Empty input (under 2 characters): Recently viewed, then Commands, then Screens.
 * - Typing: Screens and Commands filtered by label (case- and accent-insensitive substring), and —
 *   after a 200 ms debounce — Tenants and Resellers from the lists' own product-scoped server
 *   searches (`size: 5` each, in parallel). A newer query aborts the older requests.
 * - Each server group loads, fails and retries on its own: one failed search never hides the other.
 * - No results anywhere: the two "Search the list" next steps.
 * Records are shown side by side exactly as the server sent them — never merged into one verdict.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { adminApi } from '@/api/tmm/admin';
import type { CustomerRateCardView, ResellerDto } from '@/api/tmm/types';
import { useCopy } from '@/hooks/useCopy';
import { COLOR_SCHEME_MODES, type ColorSchemeMode } from '@/theme/colorScheme';
import { NAV_GROUPS, SETTINGS_NAV_ITEM } from '../nav';
import type { RecentEntry } from './useRecent';

export const PALETTE_DEBOUNCE_MS = 200;
export const PALETTE_MIN_CHARS = 2;
export const PALETTE_RECORD_LIMIT = 5;

export type ServerSource = 'partners' | 'customerRateCards';

export type PaletteItem =
  | { kind: 'partner'; id: string; reseller: ResellerDto }
  | { kind: 'customerRateCard'; id: string; cardId: number; name: string; currency: string }
  | { kind: 'recent'; id: string; entry: RecentEntry }
  | { kind: 'screen'; id: string; label: string; to: string; icon: ReactNode }
  | { kind: 'command'; id: string; label: string; to: string }
  | { kind: 'theme'; id: string; label: string; mode: ColorSchemeMode }
  | { kind: 'retry'; id: string; label: string; source: ServerSource }
  | { kind: 'listSearch'; id: string; label: string; to: string };

export interface PaletteGroup {
  id: string;
  label: string;
  items: PaletteItem[];
  /** A server group still searching: a slim progress bar in this group only. */
  loading?: boolean;
}

export interface PaletteView {
  /** The trimmed query the groups reflect ('' on the empty-input view, under 2 characters). */
  term: string;
  groups: PaletteGroup[];
  /** Every source has answered for `term` (always true on the empty-input view). */
  settled: boolean;
  /** A settled search with no match anywhere: `groups` holds only the list fallbacks. */
  noMatches: boolean;
  retry: (source: ServerSource) => void;
}

/** Lower-case, accents stripped: "Montréal" matches "montreal", "São" matches "sao". */
export function foldForMatch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function matchesLabel(label: string, term: string): boolean {
  return foldForMatch(label).includes(foldForMatch(term));
}

/** `value`, once it has stopped changing for `delayMs`. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

type Search<T> = (q: string, signal: AbortSignal) => Promise<T[]>;

const searchResellers: Search<ResellerDto> = (q, signal) =>
  adminApi
    .listResellers({ q, size: PALETTE_RECORD_LIMIT }, { signal })
    .then((page) => page.content.slice(0, PALETTE_RECORD_LIMIT));

/** The list has no `q`. Filter the names here; never a stored recent. */
const searchCustomerRateCards: Search<CustomerRateCardView> = (q, signal) =>
  adminApi
    .listCustomerRateCards({ signal })
    .then((list) =>
      list.rateCards.filter((card) => matchesLabel(card.name, q)).slice(0, PALETTE_RECORD_LIMIT),
    );

type SourceState<T> =
  { status: 'idle' | 'loading' | 'failed'; items: [] } | { status: 'ready'; items: T[] };

/** One server search for `term`; a new term or a retry aborts the request in flight. */
function useServerSearch<T>(term: string, search: Search<T>): [SourceState<T>, () => void] {
  const [attempt, setAttempt] = useState(0);
  const requestKey = term === '' ? null : `${attempt}:${term}`;
  const [response, setResponse] = useState<{
    key: string | null;
    items: T[];
    failed: boolean;
  }>({ key: null, items: [], failed: false });

  useEffect(() => {
    if (requestKey === null) return;
    const controller = new AbortController();
    search(term, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setResponse({ key: requestKey, items, failed: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) setResponse({ key: requestKey, items: [], failed: true });
      });
    return () => controller.abort();
  }, [requestKey, search, term]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  if (requestKey === null) return [{ status: 'idle', items: [] }, retry];
  if (response.key !== requestKey) return [{ status: 'loading', items: [] }, retry];
  if (response.failed) return [{ status: 'failed', items: [] }, retry];
  return [{ status: 'ready', items: response.items }, retry];
}

export function usePaletteSources(input: string, recents: RecentEntry[]): PaletteView {
  const { t } = useCopy();
  // Screens and Commands filter as typed (Enter never acts on a stale list); only the server
  // searches wait for the debounce, and read as loading until it has passed.
  const typed = input.trim();
  const term = typed.length >= PALETTE_MIN_CHARS ? typed : '';
  const debounced = useDebounced(term, PALETTE_DEBOUNCE_MS);
  const [resellerSearch, retryResellers] = useServerSearch(debounced, searchResellers);
  const [cardSearch, retryCards] = useServerSearch(debounced, searchCustomerRateCards);
  const pending: SourceState<never> = { status: 'loading', items: [] };
  const resellers = debounced === term ? resellerSearch : pending;
  const customerRateCards = debounced === term ? cardSearch : pending;
  const retry = useCallback(
    (source: ServerSource) => {
      if (source === 'customerRateCards') retryCards();
      else retryResellers();
    },
    [retryCards, retryResellers],
  );

  // The nav's own labels (SEARCH-002): the palette never renames a screen.
  const screens: PaletteItem[] = [
    ...NAV_GROUPS.flatMap((group) => group.items),
    SETTINGS_NAV_ITEM,
  ].map((item) => ({
    kind: 'screen',
    id: `screen:${item.to}`,
    label: t(item.key, item.fallback),
    to: item.to,
    icon: item.icon,
  }));
  const themeLabels: Record<ColorSchemeMode, string> = {
    light: t('shell.theme.light', 'Light'),
    dark: t('shell.theme.dark', 'Dark'),
    system: t('shell.theme.system', 'System'),
  };
  const commands: PaletteItem[] = [
    {
      kind: 'command',
      id: 'command:newPartner',
      label: t('partners.new', 'New partner…'),
      to: '/partners?create=1',
    },
    {
      kind: 'command',
      id: 'command:newRateCard',
      label: t('palette.command.newRateCard', 'New rate card'),
      to: '/rate-cards?create=1',
    },
    ...COLOR_SCHEME_MODES.map((mode): PaletteItem => ({
      kind: 'theme',
      id: `theme:${mode}`,
      label: t('palette.command.theme', 'Theme: {{mode}}', { mode: themeLabels[mode] }),
      mode,
    })),
  ];
  const labels = {
    recent: t('palette.group.recent', 'Recently viewed'),
    commands: t('palette.group.commands', 'Commands'),
    screens: t('palette.group.screens', 'Screens'),
    partners: t('page.partners.title', 'Partners'),
    customerRateCards: t('nav.rateCards', 'Rate cards'),
  };
  const failedCopy: Record<ServerSource, string> = {
    partners: t('partners.paletteFailed', "Couldn't search partners — try again"),
    customerRateCards: t('rateCard.searchFailed', "Couldn't search rate cards — try again"),
  };

  if (term === '') {
    const recent: PaletteItem[] = recents.map((entry) => ({
      kind: 'recent',
      id: `recent:${entry.type}:${entry.key}`,
      entry,
    }));
    const groups: PaletteGroup[] = [
      { id: 'recent', label: labels.recent, items: recent },
      { id: 'commands', label: labels.commands, items: commands },
      { id: 'screens', label: labels.screens, items: screens },
    ].filter((group) => group.items.length > 0);
    return { term, groups, settled: true, noMatches: false, retry };
  }

  const serverGroup = <T>(
    source: ServerSource,
    state: SourceState<T>,
    toItem: (row: T) => PaletteItem,
  ): PaletteGroup => {
    if (state.status === 'failed') {
      const label = failedCopy[source];
      return {
        id: source,
        label: labels[source],
        items: [{ kind: 'retry', id: `retry:${source}`, label, source }],
      };
    }
    return {
      id: source,
      label: labels[source],
      items: state.items.map(toItem),
      loading: state.status === 'loading',
    };
  };

  const recordGroups = [
    serverGroup('partners', resellers, (reseller) => ({
      kind: 'partner',
      id: `reseller:${reseller.id}`,
      reseller,
    })),
    serverGroup('customerRateCards', customerRateCards, (card) => ({
      kind: 'customerRateCard',
      id: `customerRateCard:${card.id}`,
      cardId: card.id,
      name: card.name,
      currency: card.currency,
    })),
  ];
  const localGroups: PaletteGroup[] = [
    {
      id: 'screens',
      label: labels.screens,
      items: screens.filter((item) => item.kind === 'screen' && matchesLabel(item.label, term)),
    },
    {
      id: 'commands',
      label: labels.commands,
      items: commands.filter(
        (item) =>
          (item.kind === 'command' || item.kind === 'theme') && matchesLabel(item.label, term),
      ),
    },
  ];
  const settled = resellers.status !== 'loading' && customerRateCards.status !== 'loading';
  // The card list has no query parameter, so a no-match fallback stays the Partners list only.
  const groups = [...recordGroups, ...localGroups].filter(
    (group) => group.items.length > 0 || group.loading,
  );

  if (settled && groups.length === 0) {
    const q = encodeURIComponent(term);
    const fallbacks: PaletteItem[] = [
      {
        kind: 'listSearch',
        id: 'listSearch:partners',
        label: t('partners.searchList', 'Search the Partners list'),
        to: `/partners?q=${q}`,
      },
    ];
    return {
      term,
      groups: [{ id: 'next', label: t('palette.group.nextSteps', 'Next steps'), items: fallbacks }],
      settled,
      noMatches: true,
      retry,
    };
  }

  return { term, groups, settled, noMatches: false, retry };
}
